import { CheckIcon } from '@phosphor-icons/react'
import { OpenInWindow } from 'iconoir-react'
import moment from 'moment'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { cn } from 'app/lib/utils'
import { isCallerIdName } from './responseQueue'
import { SnoozeControl } from './SnoozeControl'
import { ResponseRow } from './useResponseQueue'

/** Falls back to the number when the carrier sent caller-ID filler instead of a name, or when there is no contact. */
export function displayName(row: Partial<Pick<ResponseRow, `contact` | `phone`>>): string {
  const name = row.contact?.name?.trim()
  if (name && !isCallerIdName(name)) return name
  const phone = row.contact?.cellPhone ?? row.phone
  return (phone ? formatPhoneNumber(phone) : undefined) || name || `Unknown contact`
}

export function stamp(iso: string): string {
  const at = moment(iso)
  if (at.isSame(moment(), `day`)) return `Today ${at.format(`h:mm A`)}`
  if (at.isSame(moment().subtract(1, `day`), `day`)) return `Yesterday ${at.format(`h:mm A`)}`
  return at.format(`MMM D, h:mm A`)
}

export function Row({
  row,
  selected,
  onSelect,
  onOpen,
  onSnooze,
  onDone,
}: {
  row: ResponseRow
  selected: boolean
  onSelect: () => void
  onOpen: () => void
  onSnooze: (until: Date) => void
  onDone: () => void
}) {
  return (
    <div
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key !== `Enter` && event.key !== ` `) return
        // Space would scroll the pane out from under the row it just picked.
        event.preventDefault()
        onSelect()
      }}
      role="button"
      tabIndex={0}
      aria-current={selected}
      className={cn(
        `group relative cursor-pointer border-l-2 px-4 py-2.5 transition`,
        selected
          ? `border-l-blue-500 bg-blue-50`
          : `border-l-transparent hover:bg-blue-50/60 focus-visible:bg-blue-50 focus-visible:outline-none`,
      )}
    >
      <div className="flex items-baseline gap-2">
        <p className="min-w-0 flex-1 truncate text-ui-sm text-strong">
          {displayName(row)}
          {/* Only a text's read state means anything: activities are built unread,
              so a dot on every call row would say nothing. */}
          {row.channel === `sms` && row.unread && (
            <span className="ml-1.5 inline-block h-1.5 w-1.5 rounded-full bg-blue-500 align-middle" />
          )}
        </p>
        <span className="shrink-0 text-body-xs tabular-nums text-subtle">{stamp(row.since)}</span>
      </div>

      {row.channel === `sms` && (
        <p className="mt-0.5 min-w-0 truncate text-body-sm text-subtle">{row.preview.trim()}</p>
      )}

      <span
        className={cn(
          `absolute bottom-1.5 right-2 flex items-center gap-0.5 rounded-md pl-2 opacity-0 transition`,
          `group-focus-within:opacity-100 group-hover:opacity-100`,
          selected ? `bg-blue-50` : `bg-surface group-hover:bg-blue-50`,
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDone}
          aria-label="Mark as done"
          title="Mark as done"
          className="flex h-7 w-7 items-center justify-center rounded-full text-subtle transition hover:bg-green-100 hover:text-green-700"
        >
          <CheckIcon size={16} weight="bold" />
        </button>
        <SnoozeControl onSnooze={onSnooze} />
        <button
          type="button"
          onClick={onOpen}
          aria-label="Open contact"
          title="Open contact"
          className="flex h-7 w-7 items-center justify-center rounded-full text-subtle transition hover:bg-blue-100 hover:text-blue-700"
        >
          <OpenInWindow height={16} width={16} />
        </button>
      </span>
    </div>
  )
}
